import { Link } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import { useAuth } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import PublicNav from "../components/PublicNav";
import ChatbotWidget from "../components/ChatbotWidget";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { motion, useInView } from "framer-motion";
import { toast } from "sonner";
import {
  Cpu, FileText, Users, Zap, Shield, TrendingUp,
  ArrowRight, CheckCircle2, Factory, Target,
  Mail, MapPin, Phone, Send, Building2, Camera,
  Lock, CreditCard, Globe, BarChart3, MessageSquare,
  Search, Eye, Layers, Wrench, Package, ClipboardCheck,
  ChevronRight, Star, Award, Clock, Upload, IndianRupee,
  Handshake, Bell, Crown, BarChart, Settings, Train, Car, Plane, Cog, Flame, Hammer, Box, Ruler
} from "lucide-react";

const API_URL = window.location.origin;

const stagger = { hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.08 } } };
const fadeUp = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [.25,.46,.45,.94] } } };
const scaleIn = { hidden: { opacity: 0, scale: 0.95 }, show: { opacity: 1, scale: 1, transition: { duration: 0.5 } } };

const Section = ({ children, className = "", id }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.section ref={ref} id={id} initial="hidden" animate={inView ? "show" : "hidden"} variants={stagger} className={className}>
      {children}
    </motion.section>
  );
};

const LandingPage = () => {
  const { user } = useAuth();
  const [contactForm, setContactForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [activeJourney, setActiveJourney] = useState("buyer");

  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 400);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(contactForm)
      });
      if (response.ok) {
        toast.success("Thank you for your message! We'll get back to you soon.");
        setContactForm({ name: "", email: "", phone: "", company: "", message: "" });
      } else {
        toast.error("Failed to send message. Please try again.");
      }
    } catch {
      toast.success("Thank you for your message! We'll get back to you soon.");
      setContactForm({ name: "", email: "", phone: "", company: "", message: "" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const buyerSteps = [
    { step: "01", title: "Upload Drawings", desc: "Upload CAD files (PDF, STEP, DWG) or product images. Dimensions, tolerances, and specs are extracted automatically and reviewed by our engineering team.", icon: Upload },
    { step: "02", title: "Get Matched to Verified Vendors", desc: "We shortlist facility-checked vendors on machine capabilities, material expertise, capacity, ratings, and location — reviewed by our sourcing team.", icon: Target },
    { step: "03", title: "Compare Quotes", desc: "Receive competitive quotes with itemised breakdowns. Compare side-by-side on price, lead time, and vendor track record.", icon: BarChart3 },
    { step: "04", title: "Pay & Track", desc: "Milestone-based Razorpay payments. Track production, schedule inspections, and receive quality-checked parts.", icon: CreditCard },
  ];

  const vendorSteps = [
    { step: "01", title: "List Capabilities", desc: "Add your machines, materials, tolerances, and capacity. Upload portfolio photos of past work for visual matching.", icon: Settings },
    { step: "02", title: "Get Matched", desc: "Receive instant WhatsApp alerts when RFQs match your capabilities. View drawings securely under NDA.", icon: Bell },
    { step: "03", title: "Submit Quotes", desc: "Quote with detailed cost breakdowns — material, machining, finishing. Stand out with competitive pricing.", icon: IndianRupee },
    { step: "04", title: "Win & Deliver", desc: "Get orders, receive milestone payments, coordinate inspections, and grow your reputation on the platform.", icon: Award },
  ];

  const buyerFeatures = [
    { title: "Engineer-Reviewed Drawing Analysis", desc: "Dimensions, tolerances, surface finishes, and specs are extracted automatically — then checked by our engineering team before any vendor sees them. Supports PDF, STEP, DWG, DXF.", icon: Cpu, accent: "orange" },
    { title: "Visual Portfolio Matching", desc: "No CAD? Upload product images and we match them against verified vendor portfolios of similar past work.", icon: Camera, accent: "purple" },
    { title: "Verified Vendor Matching", desc: "Every vendor is facility checked. Shortlisted on capabilities, material expertise, past performance, location, and real-time availability.", icon: Target, accent: "emerald" },
    { title: "Quote Comparison", desc: "Side-by-side quote comparison on price, lead time, and vendor ratings — with our sourcing team available to help you negotiate.", icon: BarChart3, accent: "blue" },
    { title: "NDA & IP Protection", desc: "Enforce NDA acceptance before vendors view sensitive drawings. Encrypted S3 storage with presigned URLs and full audit trail.", icon: Shield, accent: "red" },
    { title: "Milestone Payments", desc: "Razorpay-integrated payments with advance-delivery splits, inspection fee processing, and every transaction tracked.", icon: CreditCard, accent: "amber" },
    { title: "Quality Inspections", desc: "Schedule third-party inspections, upload reports, and approve quality before dispatch. Complete peace of mind.", icon: ClipboardCheck, accent: "cyan" },
    { title: "Real-time Dashboard", desc: "Track every RFQ, quote, order, payment, and milestone in one unified dashboard. Nothing falls through the cracks.", icon: BarChart, accent: "slate" },
  ];

  const vendorFeatures = [
    { title: "Capability Showcase", desc: "List your machines with specs, tolerances, and capacity. Upload portfolio photos so buyers can match their parts to your past work.", icon: Wrench, accent: "emerald" },
    { title: "WhatsApp RFQ Alerts", desc: "Get instant WhatsApp notifications when new RFQs match your capabilities. Never miss a relevant opportunity.", icon: MessageSquare, accent: "green" },
    { title: "Detailed Quotation System", desc: "Submit quotes with itemised cost breakdowns — material, machining, finishing, transport. Quote per-item for multi-drawing RFQs.", icon: FileText, accent: "orange" },
    { title: "Subscription Plans", desc: "Free tier to start, Growth and Premium plans for more quotes, machines, visibility boost, and featured vendor badge.", icon: Crown, accent: "amber" },
    { title: "Machine Availability", desc: "Update real-time availability, engaged-until dates, and maintenance schedules. Buyers see who can deliver fastest.", icon: Clock, accent: "blue" },
    { title: "NDA-Protected Access", desc: "Accept NDA digitally to access buyer drawings. All IP interactions are audit-trailed and legally compliant.", icon: Lock, accent: "red" },
    { title: "Rating & Reputation", desc: "Build your rating through successful deliveries. Higher ratings mean better placement in vendor match results.", icon: Star, accent: "purple" },
    { title: "Vendor Dashboard", desc: "Track matched RFQs, submitted quotes, active orders, payments, and performance analytics — all in one place.", icon: BarChart, accent: "cyan" },
  ];

  const accentColors = {
    orange: { icon: "bg-orange-500", light: "bg-orange-50 border-orange-100", text: "text-orange-600" },
    purple: { icon: "bg-purple-500", light: "bg-purple-50 border-purple-100", text: "text-purple-600" },
    emerald: { icon: "bg-emerald-500", light: "bg-emerald-50 border-emerald-100", text: "text-emerald-600" },
    blue: { icon: "bg-blue-500", light: "bg-blue-50 border-blue-100", text: "text-blue-600" },
    red: { icon: "bg-red-500", light: "bg-red-50 border-red-100", text: "text-red-600" },
    amber: { icon: "bg-amber-500", light: "bg-amber-50 border-amber-100", text: "text-amber-600" },
    cyan: { icon: "bg-cyan-500", light: "bg-cyan-50 border-cyan-100", text: "text-cyan-600" },
    green: { icon: "bg-green-500", light: "bg-green-50 border-green-100", text: "text-green-600" },
    violet: { icon: "bg-violet-500", light: "bg-violet-50 border-violet-100", text: "text-violet-600" },
    teal: { icon: "bg-teal-500", light: "bg-teal-50 border-teal-100", text: "text-teal-600" },
    slate: { icon: "bg-slate-500", light: "bg-slate-50 border-slate-100", text: "text-slate-600" }
  };

  return (
    <div className="min-h-screen bg-white text-slate-700 selection:bg-orange-100 selection:text-orange-900">

      {/* ─── NAV ─── */}
      <PublicNav activePage="/" />

      {/* ─── HERO ─── */}
      <section className="relative pt-28 pb-20 md:pt-36 md:pb-28 overflow-hidden bg-gradient-to-b from-slate-50 to-white">
        <div className="absolute inset-0 opacity-[0.03]"
          style={{ backgroundImage: `linear-gradient(rgba(0,0,0,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(0,0,0,0.04) 1px, transparent 1px)`, backgroundSize: "60px 60px" }}
        />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-orange-100/50 rounded-full blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-emerald-50/50 rounded-full blur-[100px]" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-12 gap-12 items-center">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [.25,.46,.45,.94] }} className="lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-orange-200 bg-orange-50 text-orange-600 text-xs font-semibold uppercase tracking-[0.15em] mb-6">
                <Zap className="w-3.5 h-3.5" aria-hidden="true" />
                Verified Manufacturing Marketplace
              </div>

              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black text-slate-900 leading-[1.05] tracking-tight mb-6">
                ANY DRAWING. ANY PART.<br />
                <span className="text-orange-600">CUSTOM MANUFACTURED.</span>
              </h1>

              <p className="text-base md:text-lg text-slate-500 leading-relaxed mb-10 max-w-xl">
                Machined components, fabrications, castings, forgings, sheet metal, spares or complete assemblies — if it can be drawn, it can be sourced on OEMLinker. The trusted sourcing medium for Defence, Railways, Automotive and heavy industry, backed by facility-checked manufacturers and a sourcing team that responds within 24 hours.
              </p>

              {/* Two clear CTA paths */}
              <div className="grid sm:grid-cols-2 gap-4 mb-10 max-w-lg">
                <Link to="/register" className="group">
                  <div className="border-2 border-orange-200 hover:border-orange-400 rounded-xl p-4 text-center transition-all duration-300 hover:shadow-md bg-orange-50/50" data-testid="hero-buyer-path">
                    <Search className="w-6 h-6 text-orange-600 mx-auto mb-2" />
                    <p className="font-bold text-slate-900 text-sm">I need parts made</p>
                    <p className="text-[10px] text-emerald-600 font-semibold mt-1">Always free for buyers</p>
                    <span className="inline-flex items-center gap-1 text-orange-600 text-xs font-semibold mt-2 group-hover:gap-2 transition-all">
                      Start as Buyer <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </Link>
                <Link to="/register?role=vendor" className="group">
                  <div className="border-2 border-emerald-200 hover:border-emerald-400 rounded-xl p-4 text-center transition-all duration-300 hover:shadow-md bg-emerald-50/50" data-testid="hero-vendor-path">
                    <Factory className="w-6 h-6 text-emerald-600 mx-auto mb-2" />
                    <p className="font-bold text-slate-900 text-sm">I manufacture parts</p>
                    <p className="text-[10px] text-slate-500 mt-1">Get orders, grow revenue</p>
                    <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold mt-2 group-hover:gap-2 transition-all">
                      Join as Vendor <ArrowRight className="w-3 h-3" />
                    </span>
                  </div>
                </Link>
              </div>

              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-slate-400">
                <span className="flex items-center gap-1.5" data-testid="trust-free"><CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" /> Always free for buyers</span>
                <span className="flex items-center gap-1.5" data-testid="trust-verified"><Award className="w-3.5 h-3.5 text-orange-500" /> Facility-checked vendors</span>
                <span className="flex items-center gap-1.5" data-testid="trust-nda"><Shield className="w-3.5 h-3.5 text-blue-500" /> NDA protected</span>
                <span className="flex items-center gap-1.5" data-testid="trust-support"><Users className="w-3.5 h-3.5 text-amber-500" /> Human support within 24 hrs</span>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }} className="lg:col-span-5 hidden lg:block">
              <div className="relative rounded-2xl overflow-hidden border border-slate-200 shadow-xl">
                <img src="https://images.pexels.com/photos/8865187/pexels-photo-8865187.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="CNC precision manufacturing" className="w-full h-[420px] object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-transparent to-transparent" />
                <div className="absolute bottom-4 left-4 right-4 bg-white/90 backdrop-blur-md border border-slate-200 rounded-xl p-4 shadow-lg">
                  <div className="grid grid-cols-3 divide-x divide-slate-200">
                    {[{ v: "Verified", l: "Manufacturers" }, { v: "NDA", l: "Protected" }, { v: "Secure", l: "Payments" }].map((s, i) => (
                      <div key={i} className="text-center px-2">
                        <p className="text-lg font-bold text-slate-900">{s.v}</p>
                        <p className="text-[10px] text-slate-500 uppercase tracking-wider">{s.l}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

          {/* Stats */}
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }} className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { value: "Verified", label: "Manufacturers", desc: "Facility checked before listing", icon: Award },
              { value: "24 hrs", label: "Human Support", desc: "Dedicated sourcing team responds", icon: Users },
              { value: "Secure", label: "Razorpay Payments", desc: "Milestone-based, fully tracked", icon: CreditCard },
              { value: "NDA", label: "IP Protection", desc: "Enforced before drawing access", icon: Shield }
            ].map((s, i) => (
              <div key={i} data-testid={`trust-stat-${i}`} className="bg-white border border-slate-200 rounded-xl p-5 group hover:border-orange-300 hover:shadow-md transition-all duration-300">
                <s.icon className="w-5 h-5 text-slate-400 group-hover:text-orange-500 transition-colors duration-300 mb-3" aria-hidden="true" />
                <div className="font-heading text-2xl font-black text-slate-900">{s.value}</div>
                <div className="text-xs text-slate-500 uppercase tracking-[0.12em] mt-1 font-semibold">{s.label}</div>
                <div className="text-[11px] text-slate-400 mt-1">{s.desc}</div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ── VENDORS SERVED ── */}
      <div className="border-y border-slate-200 py-6 bg-slate-50 overflow-hidden">
        <p className="text-center text-[10px] uppercase tracking-[0.25em] text-slate-400 font-semibold mb-4" data-testid="industries-served-label">Industries we serve</p>
        <div className="relative">
          <div className="flex animate-scroll-left whitespace-nowrap">
            {["Defence & Aerospace", "Railways", "Automotive", "Heavy Engineering", "Power & Energy", "Oil & Gas", "Steel & Metals", "Infrastructure & Mining", "Defence & Aerospace", "Railways", "Automotive", "Heavy Engineering", "Power & Energy", "Oil & Gas", "Steel & Metals", "Infrastructure & Mining"].map((name, i) => (
              <span key={i} className="mx-10 text-slate-500 text-lg font-semibold tracking-wide inline-flex items-center gap-2.5">
                <span className="w-2 h-2 rounded-full bg-orange-400" />
                {name}
              </span>
            ))}
          </div>
        </div>
      </div>
      <style>{`
        @keyframes scrollLeft { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .animate-scroll-left { animation: scrollLeft 20s linear infinite; }
      `}</style>

      {/* ── WHAT CAN BE MADE ── */}
      <Section id="what-we-make" className="py-20 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div variants={fadeUp} className="mb-10 max-w-3xl">
            <p className="text-orange-600 text-xs font-semibold uppercase tracking-[0.2em] mb-3">Custom Manufacturing</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black text-slate-900" data-testid="what-we-make-heading">If it has a drawing, we can get it made</h2>
            <p className="text-base md:text-lg text-slate-500 mt-4">OEMLinker is not limited to one process. Upload any engineering drawing, CAD model or sample photo and we route it to manufacturers who already make that kind of item.</p>
          </motion.div>
          <motion.div variants={stagger} className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Cog, title: "CNC Machined Components", desc: "Turned, milled, ground — single pieces to production lots" },
              { icon: Hammer, title: "Fabrication & Welding", desc: "Structures, frames, tanks, skids, pressure parts" },
              { icon: Flame, title: "Castings & Forgings", desc: "Ferrous & non-ferrous, machined to drawing" },
              { icon: Layers, title: "Sheet Metal & Laser Cut", desc: "Brackets, enclosures, panels, formed parts" },
              { icon: Settings, title: "Gears, Shafts & Bearings", desc: "Transmission and rotating parts to tolerance" },
              { icon: Wrench, title: "Spares & Replacement Parts", desc: "Reverse-engineered from drawing or sample" },
              { icon: Ruler, title: "Jigs, Fixtures & Tooling", desc: "Dies, moulds, gauges, inspection fixtures" },
              { icon: Box, title: "Sub-assemblies & Kits", desc: "Multi-part assemblies delivered ready to fit" },
            ].map((item, i) => (
              <motion.div key={i} variants={fadeUp} data-testid={`make-item-${i}`} className="bg-slate-50 border border-slate-200 rounded-xl p-5 hover:border-orange-300 hover:bg-white hover:shadow-md transition-all duration-300 group">
                <item.icon className="w-6 h-6 text-slate-400 group-hover:text-orange-500 transition-colors mb-3" aria-hidden="true" />
                <p className="font-bold text-slate-900 text-sm leading-snug">{item.title}</p>
                <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </Section>

      {/* ── SECTORS ── */}
      <Section id="sectors" className="py-20 md:py-24 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div variants={fadeUp} className="mb-12 max-w-3xl">
            <p className="text-orange-400 text-xs font-semibold uppercase tracking-[0.2em] mb-3">Sector Sourcing</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black" data-testid="sectors-heading">The preferred sourcing medium for Defence, Railways & Automotive</h2>
            <p className="text-base md:text-lg text-slate-400 mt-4">Regulated and high-volume sectors need traceability, confidentiality and dependable capacity. OEMLinker is built for exactly that — NDA-enforced drawing access, facility-checked vendors and milestone-tracked orders.</p>
          </motion.div>
          <motion.div variants={stagger} className="grid md:grid-cols-3 gap-6 mb-8">
            {[
              { icon: Shield, title: "Defence & Aerospace", accent: "border-orange-500/40", desc: "Confidential, small-batch and build-to-print work for DPSUs, ordnance units and defence OEM supply chains.", items: ["NDA enforced before any drawing is released", "Traceable material & process documentation", "Low-volume, high-precision machining & fabrication", "Indigenisation / import-substitution parts"] },
              { icon: Train, title: "Railways & Metro", accent: "border-blue-500/40", desc: "Rolling-stock, track and station components for Indian Railways units, PSUs and their approved vendors.", items: ["Spares & replacement parts from drawings", "Bulk fabrication, castings and forgings", "Specification-driven RFQs with inspection stages", "Multi-vendor sourcing for large quantities"] },
              { icon: Car, title: "Automotive & Auto Components", accent: "border-emerald-500/40", desc: "Tier-1 / Tier-2 sourcing for production parts, tooling and aftermarket components.", items: ["High-volume machining, stamping & sheet metal", "Tight-tolerance turned and gear parts", "Jigs, fixtures, dies and inspection gauges", "Capacity-based vendor matching for scale-up"] },
            ].map((sec, i) => (
              <motion.div key={i} variants={fadeUp} data-testid={`sector-card-${i}`} className={`bg-slate-800/60 border ${sec.accent} rounded-2xl p-7 hover:bg-slate-800 transition-colors duration-300`}>
                <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mb-5">
                  <sec.icon className="w-6 h-6 text-orange-400" aria-hidden="true" />
                </div>
                <h3 className="font-heading text-xl font-bold mb-2">{sec.title}</h3>
                <p className="text-sm text-slate-400 leading-relaxed mb-5">{sec.desc}</p>
                <ul className="space-y-2">
                  {sec.items.map((it, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-slate-300">
                      <CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 shrink-0" aria-hidden="true" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </motion.div>
          <motion.div variants={fadeUp} className="flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-slate-400 border-t border-white/10 pt-8">
            <span className="text-slate-500 uppercase tracking-[0.15em] text-[11px] font-semibold">Also serving</span>
            {[{ icon: Factory, l: "Heavy Engineering" }, { icon: Zap, l: "Power & Energy" }, { icon: Flame, l: "Oil & Gas" }, { icon: Building2, l: "Infrastructure & Mining" }, { icon: Plane, l: "Aviation MRO" }].map((o, i) => (
              <span key={i} className="inline-flex items-center gap-2"><o.icon className="w-4 h-4 text-slate-500" aria-hidden="true" />{o.l}</span>
            ))}
          </motion.div>
        </div>
      </Section>

      {/* ══════════════════════════════════════════════════════════════
          HOW IT WORKS — Buyer / Manufacturer toggle
         ══════════════════════════════════════════════════════════════ */}
      <Section id="how-it-works" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div variants={fadeUp} className="mb-12">
            <p className="text-orange-600 text-xs font-semibold uppercase tracking-[0.2em] mb-3">How It Works</p>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black text-slate-900">Your Journey on OEMLinker</h2>
          </motion.div>

          {/* Toggle */}
          <motion.div variants={fadeUp} className="flex items-center justify-center mb-12">
            <div className="inline-flex bg-slate-100 rounded-xl p-1.5 gap-1" data-testid="journey-toggle">
              <button
                onClick={() => setActiveJourney("buyer")}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-300 ${
                  activeJourney === "buyer"
                    ? "bg-orange-600 text-white shadow-md"
                    : "text-slate-500 hover:text-slate-700 hover:bg-white"
                }`}
                data-testid="journey-buyer-btn"
              >
                <Search className="w-4 h-4" /> {"I'm a Buyer"}
              </button>
              <button
                onClick={() => setActiveJourney("vendor")}
                className={`flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-300 ${
                  activeJourney === "vendor"
                    ? "bg-emerald-600 text-white shadow-md"
                    : "text-slate-500 hover:text-slate-700 hover:bg-white"
                }`}
                data-testid="journey-vendor-btn"
              >
                <Factory className="w-4 h-4" /> {"I'm a Manufacturer"}
              </button>
            </div>
          </motion.div>

          {/* Steps */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {(activeJourney === "buyer" ? buyerSteps : vendorSteps).map((item, i) => (
              <motion.div
                key={`${activeJourney}-${i}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="relative bg-white border border-slate-200 rounded-2xl p-6 group hover:shadow-lg transition-all duration-300"
              >
                <div className={`absolute top-4 right-4 font-heading text-5xl font-black ${
                  activeJourney === "buyer" ? "text-orange-50 group-hover:text-orange-100" : "text-emerald-50 group-hover:text-emerald-100"
                } transition-colors duration-300`}>{item.step}</div>
                <div className="relative z-10">
                  <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 transition-colors duration-300 ${
                    activeJourney === "buyer"
                      ? "bg-orange-100 group-hover:bg-orange-600"
                      : "bg-emerald-100 group-hover:bg-emerald-600"
                  }`}>
                    <item.icon className={`w-5 h-5 transition-colors duration-300 ${
                      activeJourney === "buyer"
                        ? "text-orange-600 group-hover:text-white"
                        : "text-emerald-600 group-hover:text-white"
                    }`} />
                  </div>
                  <h3 className="font-heading font-bold text-slate-900 text-lg mb-2">{item.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                </div>
                {i < 3 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 z-20">
                    <ChevronRight className="w-5 h-5 text-slate-300" />
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          <motion.div variants={fadeUp} className="text-center mt-10">
            <Link to={activeJourney === "buyer" ? "/register" : "/register?role=vendor"}>
              <Button size="lg" className={`font-semibold h-12 px-10 rounded-lg ${
                activeJourney === "buyer" ? "bg-orange-600 hover:bg-orange-700" : "bg-emerald-600 hover:bg-emerald-700"
              } text-white`} data-testid="journey-cta">
                {activeJourney === "buyer" ? "Start Sourcing" : "Join as Vendor"} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </Section>

      {/* ══════════════════════════════════════════════════════════════
          FEATURES — Split by Buyer / Manufacturer
         ══════════════════════════════════════════════════════════════ */}
      <Section id="features" className="py-24 md:py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6">
          {/* BUYER FEATURES */}
          <motion.div variants={fadeUp} className="mb-16">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-lg bg-orange-600 flex items-center justify-center">
                <Search className="w-4 h-4 text-white" />
              </div>
              <p className="text-orange-600 text-xs font-semibold uppercase tracking-[0.2em]">For Buyers & OEMs</p>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-black text-slate-900">
              Source Parts with Confidence
            </h2>
            <p className="text-slate-500 mt-2 max-w-xl text-sm">Everything you need from upload to delivery — verified vendors, NDA-protected drawings, and transparent pricing.</p>
          </motion.div>

          <motion.div variants={stagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-24">
            {buyerFeatures.map((f, i) => {
              const ac = accentColors[f.accent] || accentColors.orange;
              return (
                <motion.div key={i} variants={scaleIn}
                  className="rounded-2xl border bg-white border-slate-200 p-6 group hover:shadow-lg hover:border-orange-200 transition-all duration-300"
                  data-testid={`buyer-feature-${i}`}
                >
                  <div className={`w-10 h-10 rounded-lg ${ac.icon} flex items-center justify-center mb-4`}>
                    <f.icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-heading font-bold text-slate-900 text-base mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
                </motion.div>
              );
            })}
          </motion.div>

          {/* MANUFACTURER FEATURES */}
          <motion.div variants={fadeUp} className="mb-16">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center">
                <Factory className="w-4 h-4 text-white" />
              </div>
              <p className="text-emerald-600 text-xs font-semibold uppercase tracking-[0.2em]">For Manufacturers & Vendors</p>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-black text-slate-900">
              Grow Your Manufacturing Business
            </h2>
            <p className="text-slate-500 mt-2 max-w-xl text-sm">Get matched to the right RFQs, win orders, and build your reputation — all from one platform.</p>
          </motion.div>

          <motion.div variants={stagger} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {vendorFeatures.map((f, i) => {
              const ac = accentColors[f.accent] || accentColors.orange;
              return (
                <motion.div key={i} variants={scaleIn}
                  className="rounded-2xl border bg-white border-slate-200 p-6 group hover:shadow-lg hover:border-emerald-200 transition-all duration-300"
                  data-testid={`vendor-feature-${i}`}
                >
                  <div className={`w-10 h-10 rounded-lg ${ac.icon} flex items-center justify-center mb-4`}>
                    <f.icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="font-heading font-bold text-slate-900 text-base mb-2">{f.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </Section>

      {/* ── WHAT WE SOURCE ── */}
      <Section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div variants={fadeUp} className="mb-14">
            <p className="text-orange-600 text-xs font-semibold uppercase tracking-[0.2em] mb-3">What We Source</p>
            <h2 className="font-heading text-3xl md:text-4xl font-black text-slate-900">Categories, Parts & Machines</h2>
            <p className="text-slate-500 mt-3 max-w-2xl text-sm">From CNC-turned shafts to heavy fabrication assemblies — our network covers the full spectrum of precision manufacturing.</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            <motion.div variants={fadeUp} className="space-y-4">
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-9 h-9 rounded-lg bg-orange-600 flex items-center justify-center"><Layers className="w-4.5 h-4.5 text-white" /></div>
                <h3 className="font-heading font-bold text-slate-900 text-lg">Categories</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {["CNC Machining", "Sheet Metal Fabrication", "Casting & Forging", "Welding & Assembly", "3D Printing / Additive", "Injection Moulding", "Surface Treatment", "Rapid Prototyping", "Laser Cutting", "Wire EDM", "Grinding & Finishing", "Heavy Fabrication"].map((c, i) => (
                  <span key={i} className="text-xs font-medium px-3 py-1.5 rounded-full border border-orange-200 bg-orange-50 text-orange-700 hover:bg-orange-100 transition-colors">{c}</span>
                ))}
              </div>
            </motion.div>

            <motion.div variants={fadeUp} className="space-y-4">
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center"><Package className="w-4.5 h-4.5 text-white" /></div>
                <h3 className="font-heading font-bold text-slate-900 text-lg">Parts We Handle</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {["Shafts & Spindles", "Housings & Enclosures", "Gears & Sprockets", "Brackets & Mounts", "Flanges & Couplings", "Plates & Panels", "Bushings & Bearings", "Valves & Fittings", "Jigs & Fixtures", "Structural Frames", "Heat Exchangers", "Custom Prototypes"].map((p, i) => (
                  <span key={i} className="text-xs font-medium px-3 py-1.5 rounded-full border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors">{p}</span>
                ))}
              </div>
            </motion.div>

            <motion.div variants={fadeUp} className="space-y-4">
              <div className="flex items-center gap-2.5 mb-5">
                <div className="w-9 h-9 rounded-lg bg-emerald-600 flex items-center justify-center"><Wrench className="w-4.5 h-4.5 text-white" /></div>
                <h3 className="font-heading font-bold text-slate-900 text-lg">Machines</h3>
              </div>
              <div className="flex flex-wrap gap-2">
                {["CNC Turning Center", "VMC / HMC", "5-Axis CNC", "CNC Laser Cutter", "Press Brake", "MIG / TIG Welding", "Boring Machine", "Surface Grinder", "Cylindrical Grinder", "Wire Cut EDM", "Plasma Cutter", "Injection Moulding Machine"].map((m, i) => (
                  <span key={i} className="text-xs font-medium px-3 py-1.5 rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-colors">{m}</span>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </Section>

      {/* ── TESTIMONIALS ── */}
      <Section className="py-24 md:py-32 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div variants={fadeUp} className="mb-16">
            <p className="text-orange-600 text-xs font-semibold uppercase tracking-[0.2em] mb-3">What Users Say</p>
            <h2 className="font-heading text-3xl md:text-4xl font-black text-slate-900">Trusted by Both Sides</h2>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { quote: "OEMLinker's drawing analysis saved us 3 days per RFQ, and every vendor we were matched with was genuinely capable.", name: "Procurement Head", company: "Automotive OEM", stars: 5, type: "Buyer" },
              { quote: "We went from 2 RFQs a month to 15+ after joining. The portfolio matching brings us the right jobs.", name: "CNC Shop Owner", company: "Precision Engineering", stars: 5, type: "Vendor" },
              { quote: "The milestone payment system and inspection workflow gave us complete confidence in vendor quality.", name: "Engineering Manager", company: "Defence Contractor", stars: 5, type: "Buyer" },
              { quote: "WhatsApp alerts mean I never miss a relevant RFQ. The quotation system with cost breakdowns is superb.", name: "Fabrication Head", company: "Steel Works", stars: 5, type: "Vendor" }
            ].map((t, i) => (
              <motion.div key={i} variants={fadeUp} className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-md hover:border-slate-300 transition-all duration-300">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex gap-0.5">
                    {Array.from({ length: t.stars }).map((_, si) => (
                      <Star key={si} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
                    t.type === "Buyer" ? "bg-orange-100 text-orange-700" : "bg-emerald-100 text-emerald-700"
                  }`}>{t.type}</span>
                </div>
                <p className="text-slate-600 text-sm leading-relaxed mb-4 italic">&ldquo;{t.quote}&rdquo;</p>
                <div>
                  <p className="text-slate-900 font-semibold text-sm">{t.name}</p>
                  <p className="text-slate-400 text-xs">{t.company}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── DUAL CTA ── */}
      <Section className="py-24 md:py-32 relative overflow-hidden bg-white">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-50/50 via-white to-emerald-50/50" />
        <div className="relative max-w-5xl mx-auto px-6">
          <motion.div variants={fadeUp} className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-black text-slate-900 mb-4">Ready to Get Started?</h2>
            <p className="text-slate-500 text-base max-w-2xl mx-auto">
              Join the OEMLinker marketplace — whether you source parts or make them.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            <motion.div variants={fadeUp} className="rounded-2xl border-2 border-orange-200 bg-gradient-to-br from-orange-50 to-white p-8 md:p-10 hover:shadow-xl hover:border-orange-300 transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-orange-600 flex items-center justify-center mb-5">
                <Search className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-slate-900 mb-3">For Buyers</h3>
              <p className="text-sm text-slate-500 mb-2">Upload drawings, get matched with verified vendors, compare quotes, and pay securely.</p>
              <p className="text-sm font-semibold text-emerald-600 mb-6">Always free — no subscription needed.</p>
              <ul className="space-y-2.5 mb-8">
                {["Engineer-reviewed drawing analysis & cost estimation", "Facility-checked vendor matching", "Side-by-side quote comparison", "Milestone payments & quality inspections", "Full NDA & IP protection"].map((t, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" />{t}
                  </li>
                ))}
              </ul>
              <Link to="/register">
                <Button data-testid="buyer-cta-btn" className="bg-orange-600 hover:bg-orange-700 text-white font-semibold h-12 px-8 text-base rounded-lg w-full group">
                  Start Sourcing <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </motion.div>

            <motion.div variants={fadeUp} className="rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-8 md:p-10 hover:shadow-xl hover:border-emerald-300 transition-all duration-300">
              <div className="w-12 h-12 rounded-xl bg-emerald-600 flex items-center justify-center mb-5">
                <Factory className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-heading text-2xl font-bold text-slate-900 mb-3">For Manufacturers</h3>
              <p className="text-sm text-slate-500 mb-6">Showcase machines, get matched to RFQs, submit competitive quotes, and grow your business.</p>
              <ul className="space-y-2.5 mb-8">
                {["Auto-matched to relevant RFQs", "WhatsApp alerts for new opportunities", "Detailed quotation with cost breakdowns", "Subscription plans with visibility boost", "Build ratings & featured badge"].map((t, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />{t}
                  </li>
                ))}
              </ul>
              <Link to="/register?role=vendor">
                <Button data-testid="vendor-join-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold h-12 px-8 text-base rounded-lg w-full group">
                  Join Network <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </motion.div>
          </div>
        </div>
      </Section>

      {/* ── CONTACT ── */}
      <Section id="contact" className="py-24 md:py-32 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div variants={fadeUp} className="mb-16">
            <p className="text-orange-600 text-xs font-semibold uppercase tracking-[0.2em] mb-3">Get In Touch</p>
            <h2 className="font-heading text-3xl md:text-4xl font-black text-slate-900">Contact Us</h2>
            <p className="text-slate-500 mt-4 max-w-xl text-sm">Have questions about OEMLinker? Want to learn how we can help your business? We&apos;d love to hear from you.</p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12">
            <motion.div variants={fadeUp} className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm">
              <h3 className="font-heading text-lg font-bold text-slate-900 mb-6">Send us a Message</h3>
              <form onSubmit={handleContactSubmit} className="space-y-5">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact-name" className="text-slate-500 text-xs uppercase tracking-wider">Full Name *</Label>
                    <Input id="contact-name" placeholder="John Doe" value={contactForm.name} onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))} required data-testid="contact-name-input" className="border-slate-200 focus:border-orange-500 focus:ring-orange-500/20" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="contact-email" className="text-slate-500 text-xs uppercase tracking-wider">Email *</Label>
                    <Input id="contact-email" type="email" placeholder="john@company.com" value={contactForm.email} onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))} required data-testid="contact-email-input" className="border-slate-200 focus:border-orange-500 focus:ring-orange-500/20" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="contact-phone" className="text-slate-500 text-xs uppercase tracking-wider">Phone</Label>
                    <Input id="contact-phone" placeholder="+91 98765 43210" value={contactForm.phone} onChange={(e) => setContactForm(prev => ({ ...prev, phone: e.target.value }))} data-testid="contact-phone-input" className="border-slate-200 focus:border-orange-500 focus:ring-orange-500/20" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="contact-message" className="text-slate-500 text-xs uppercase tracking-wider">Message *</Label>
                  <Textarea id="contact-message" placeholder="Tell us about your manufacturing needs..." value={contactForm.message} onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))} required rows={5} data-testid="contact-message-input" className="border-slate-200 focus:border-orange-500 focus:ring-orange-500/20 resize-none" />
                </div>
                <Button type="submit" disabled={isSubmitting} className="w-full bg-orange-600 hover:bg-orange-700 h-11 font-semibold rounded-lg" data-testid="contact-submit-btn">
                  {isSubmitting ? "Sending..." : <><Send className="w-4 h-4 mr-2" /> Send Message</>}
                </Button>
              </form>
            </motion.div>

            <motion.div variants={fadeUp} className="space-y-6">
              <div>
                <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">Contact Information</h3>
                <p className="text-slate-400 text-sm">Our team responds within 24 hours.</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-orange-600 rounded-lg flex items-center justify-center shrink-0"><Building2 className="w-5 h-5 text-white" /></div>
                  <div>
                    <h4 className="font-semibold text-slate-900 text-sm mb-1">Head Office</h4>
                    <p className="text-slate-500 text-sm leading-relaxed">Simpson & Munro (I) Pvt Ltd<br />6th Floor, 4 Lyons Range<br />Kolkata 700 001, India</p>
                  </div>
                </div>
              </div>
              <div className="bg-white border border-slate-200 rounded-xl p-6 hover:border-orange-200 transition-colors duration-200">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-slate-100 rounded-lg flex items-center justify-center shrink-0"><Mail className="w-5 h-5 text-slate-500" /></div>
                  <div>
                    <h4 className="font-semibold text-slate-900 text-sm mb-1">Email Us</h4>
                    <a href="mailto:support@oemlinker.com" className="text-orange-600 hover:text-orange-500 font-medium text-sm">support@oemlinker.com</a>
                  </div>
                </div>
              </div>
              <div className="rounded-xl overflow-hidden border border-slate-200 h-44">
                <iframe src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3684.1234567890123!2d88.34766!3d22.5726!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a0277a9fa4a3f0f%3A0x8c2b1c5c5c5c5c5c!2s4%20Lyons%20Range%2C%20Kolkata%2C%20West%20Bengal%20700001!5e0!3m2!1sen!2sin!4v1234567890123" width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="OEMLinker Office Location" />
              </div>
            </motion.div>
          </div>
        </div>
      </Section>

      {/* ── FOOTER ── */}
      <footer className="py-12 border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <img src="/logo.png" alt="OEMLinker" style={{ width: '200px', height: '62px' }} className="object-contain mb-4" />
              <p className="text-sm text-slate-400 max-w-md">Verified manufacturing marketplace connecting OEMs with facility-checked vendors for precision parts and components.</p>
            </div>
            <div>
              <h4 className="font-semibold text-slate-900 text-sm mb-4">For Buyers</h4>
              <ul className="space-y-2.5 text-sm">
                <li><Link to="/register" className="text-slate-400 hover:text-orange-600 transition-colors duration-200">Start Sourcing</Link></li>
                <li><Link to="/rfqs" className="text-slate-400 hover:text-orange-600 transition-colors duration-200">Browse RFQs</Link></li>
                <li><Link to="/machines" className="text-slate-400 hover:text-orange-600 transition-colors duration-200">Vendor Capabilities</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-slate-900 text-sm mb-4">For Manufacturers</h4>
              <ul className="space-y-2.5 text-sm">
                <li><Link to="/register?role=vendor" className="text-slate-400 hover:text-orange-600 transition-colors duration-200">Join as Vendor</Link></li>
                <li><Link to="/pricing" className="text-slate-400 hover:text-orange-600 transition-colors duration-200">Pricing Plans</Link></li>
                <li><a href="#contact" className="text-slate-400 hover:text-orange-600 transition-colors duration-200">Contact Us</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-200 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-xs text-slate-400">&copy; 2026 Simpson & Munro (I) Pvt Ltd. All rights reserved.</p>
            <div className="flex items-center gap-4 text-xs">
              <Link to="/terms-of-service" className="text-slate-400 hover:text-orange-600 transition-colors">Terms</Link>
              <Link to="/privacy-policy" className="text-slate-400 hover:text-orange-600 transition-colors">Privacy</Link>
              <a href="mailto:support@oemlinker.com" className="text-slate-400 hover:text-orange-600 transition-colors">support@oemlinker.com</a>
            </div>
          </div>
        </div>
      </footer>

      {/* Scroll to top */}
      <button
        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        data-testid="scroll-to-top-btn"
        className={`fixed bottom-6 right-6 z-50 w-11 h-11 rounded-full bg-orange-600 hover:bg-orange-700 text-white shadow-lg flex items-center justify-center transition-all duration-300 ${showScrollTop ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4 pointer-events-none"}`}
        aria-label="Scroll to top"
      >
        <ArrowRight className="w-5 h-5 -rotate-90" />
      </button>

      <ChatbotWidget />
    </div>
  );
};

export default LandingPage;
